import { env } from '@/lib/env';
import type { SpeechModel } from 'ai';
import { ElevenLabsClient } from 'elevenlabs';

type ElevenLabsModel =
  | 'eleven_multilingual_v2'
  | 'eleven_flash_v2_5'
  | 'eleven_turbo_v2_5';

export const elevenLabsVoices: Record<string, string> = {
  alice: 'Xb7hH8MSUJpSbSDYk0k2',
  aria: '9BWtsMINqrJLrRacOk9x',
  bill: 'pqHfZKP75CvOlQylNhV4',
  brian: 'nPczCjzI2devNBz1zQrb',
  callum: 'N2lVS1w4EtoT3dr4eOWO',
  charlie: 'IKne3meq5aSn9XLyUdCD',
  charlotte: 'XB0fDUnXU5powFXDhCwa',
  chris: 'iP95p4xoKVk53GoZ742B',
  daniel: 'onwK4e9ZLuTAKqWW03F9',
  eric: 'cjVigY5qzO86Huf0OWal',
  george: 'JBFqnCBsd6RMkjVDRZzb',
  jessica: 'cgSgspJ2msm6clMCkdW9',
  laura: 'FGY2WhTYpPnrIDTdsKH5',
  liam: 'TX3LPaxmHKxFdv7VOQHJ',
  lily: 'pFZP5JQG7iQjIQuC4Bku',
  matilda: 'XrExE9yKIg1WjnnlVkGX',
  river: 'SAz9YHcvj6GT2YYXdXww',
  roger: 'CwhRBWXzGAHq8TQ4Fs17',
  sarah: 'EXAVITQu4vr4xnSDxMaL',
  will: 'bIHbv24MWmeRgasZH58o',
};

export const elevenlabs = {
  speech: (modelId: ElevenLabsModel): SpeechModel => ({
    specificationVersion: 'v1',
    provider: 'elevenlabs',
    modelId,
    doGenerate: async ({
      text,
      voice,
      outputFormat,
      instructions,
      speed,
      providerOptions,
      abortSignal,
      headers,
    }) => {
      console.log('🚀 Starting ElevenLabs speech generation...');
      console.log('🎵 Model:', modelId);
      console.log('📝 Text:', text);
      console.log('🗣️ Voice:', voice);

      if (!env.ELEVENLABS_API_KEY) {
        throw new Error('ELEVENLABS_API_KEY is not defined');
      }

      // Get voice ID from voice name or use default
      const voiceId =
        voice && voice in elevenLabsVoices
          ? elevenLabsVoices[voice]
          : elevenLabsVoices.george; // Default to George voice

      console.log('🎤 Using voice ID:', voiceId);
      console.log('🔑 API Key exists:', !!env.ELEVENLABS_API_KEY);
      console.log(
        '🔑 API Key prefix:',
        `${env.ELEVENLABS_API_KEY.substring(0, 10)}...`
      );

      const elevenlabsClient = new ElevenLabsClient({
        apiKey: env.ELEVENLABS_API_KEY,
      });

      console.log('⚡ Making request to ElevenLabs API...');

      // Convert outputFormat from AI SDK format to ElevenLabs format
      let elevenLabsFormat:
        | 'mp3_44100_128'
        | 'pcm_44100'
        | 'pcm_16000'
        | 'pcm_22050'
        | undefined = 'mp3_44100_128'; // Default to MP3

      if (outputFormat) {
        switch (outputFormat) {
          case 'mp3':
            elevenLabsFormat = 'mp3_44100_128';
            break;
          case 'wav':
            elevenLabsFormat = 'pcm_44100';
            break;
          case 'aac':
            elevenLabsFormat = 'mp3_44100_128'; // fallback to mp3 if aac not supported
            break;
          default:
            elevenLabsFormat = 'mp3_44100_128';
        }
      }

      let audioData: Uint8Array;

      try {
        // Simplified request structure - let's start with minimal params
        const requestParams = {
          text,
          model_id: modelId,
          output_format: elevenLabsFormat,
          voice_settings: {
            stability: 0.5,
            similarity_boost: 0.5,
          },
        };

        console.log(
          '📝 Request params:',
          JSON.stringify(requestParams, null, 2)
        );

        const audioStream = await elevenlabsClient.textToSpeech.convert(
          voiceId,
          requestParams
        );

        console.log('📥 Converting audio stream to buffer...');

        // Convert the Node.js Readable stream to buffer
        const chunks: Buffer[] = [];

        for await (const chunk of audioStream) {
          chunks.push(chunk);
        }

        // Combine all chunks into a single buffer and convert to Uint8Array
        const audioBuffer = Buffer.concat(chunks);
        audioData = new Uint8Array(audioBuffer);
      } catch (error) {
        const errorDetails = {
          error,
          message: error instanceof Error ? error.message : 'Unknown error',
          statusCode:
            error && typeof error === 'object' && 'statusCode' in error
              ? error.statusCode
              : undefined,
          body:
            error && typeof error === 'object' && 'body' in error
              ? error.body
              : undefined,
          voiceId,
          modelId,
        };

        console.log('❌ ElevenLabs API error details:', errorDetails);

        // More detailed error message
        let errorMessage = 'ElevenLabs API error';
        if (error instanceof Error) {
          errorMessage += `: ${error.message}`;
        }
        if (errorDetails.statusCode === 403) {
          errorMessage +=
            ' - This might be due to: 1) Invalid API key, 2) Insufficient permissions, 3) Invalid voice ID, or 4) Model not available for your account tier';
        }

        throw new Error(errorMessage);
      }

      console.log('💾 Audio buffer size:', audioData.byteLength, 'bytes');
      console.log('🎉 Speech generation completed successfully!');

      const warnings = [];

      // Add warnings for unsupported features
      if (instructions && !providerOptions?.elevenlabs?.instructions) {
        warnings.push({
          type: 'unsupported-setting' as const,
          setting: 'instructions' as const,
          details: 'Instructions are not directly supported by ElevenLabs API',
        });
      }

      return {
        audio: audioData,
        warnings,
        request: {
          body: {
            text,
            model_id: modelId,
            output_format: elevenLabsFormat,
            voice_settings: {
              stability: 0.5,
              similarity_boost: 0.5,
            },
          },
        },
        response: {
          timestamp: new Date(),
          modelId,
          headers: undefined, // Client SDK doesn't expose headers
          body: undefined, // Binary data, not JSON
        },
        providerMetadata: {
          elevenlabs: {
            voiceId,
            model: modelId,
          },
        },
      };
    },
  }),
};
